/**
 * Calculate Modules For Box
 *
 * Pure function that calculates module placement within a rectangular box.
 * Supports both roof-attached mode (with polygon containment) and free-field mode.
 */

import { isPointInPolygon } from '../geometry/polygonCalculations';
import { createModuleRectangle } from './moduleGridCalculator';

// ============================================================================
// Types
// ============================================================================

export interface SolarModule {
  position: [number, number];
  width: number;
  height: number;
  orientation: 'portrait' | 'landscape';
  rotation: number;
  azimuth: number;
  moduleBrand?: string;
  moduleModel?: string;
  modulePowerWatts?: number;
  segmentId?: number;
}

export interface ModuleConfig {
  moduleWidth: number;
  moduleHeight: number;
  moduleOrientation: 'portrait' | 'landscape';
  moduleSpacing: number;
  rowSpacingMultiplier: number;
  modulePowerWatts: number;
  obstructionSetback: number;
  edgeSetback: number;
}

export interface SegmentConfig {
  moduleSpacing: number;
  rowSpacing: number;
  targetCount: number | null;
}

export interface PanelInfo {
  brand?: string;
  model?: string;
  power_watts?: number;
}

export interface CalculateModulesInput {
  box: [number, number][];
  moduleConfig: ModuleConfig;
  segmentRotation?: number;
  segmentConfig?: SegmentConfig;
  freeFieldMode?: boolean;
  applyFillPercentage?: boolean;
  targetModuleCount?: number | null;
  polygonCoords?: [number, number][];
  obstructions?: [number, number][][];
  panelInfo?: PanelInfo;
}

// ============================================================================
// Main Function
// ============================================================================

/**
 * Calculate module placements within a box
 *
 * @param input - All required parameters for calculation
 * @returns Array of SolarModule placements
 */
export function calculateModulesForBox(input: CalculateModulesInput): SolarModule[] {
  const {
    box,
    moduleConfig,
    segmentRotation,
    segmentConfig,
    freeFieldMode = false,
    applyFillPercentage = true,
    targetModuleCount,
    polygonCoords = [],
    obstructions = [],
    panelInfo,
  } = input;

  const {
    moduleWidth,
    moduleHeight,
    moduleOrientation,
    moduleSpacing,
    rowSpacingMultiplier,
    modulePowerWatts,
    obstructionSetback,
    edgeSetback,
  } = moduleConfig;

  // Calculate box center
  const boxLats = box.map(([lat]) => lat);
  const boxLngs = box.map(([, lng]) => lng);
  const centerLat = boxLats.reduce((a, b) => a + b, 0) / boxLats.length;
  const centerLng = boxLngs.reduce((a, b) => a + b, 0) / boxLngs.length;

  const latDegPerMeter = 1 / 111000;
  const lngDegPerMeter = 1 / (111000 * Math.cos(centerLat * Math.PI / 180));

  // Calculate effective dimensions based on orientation
  const effectiveWidth = moduleOrientation === 'landscape' ? moduleHeight : moduleWidth;
  const effectiveHeight = moduleOrientation === 'landscape' ? moduleWidth : moduleHeight;

  // Use segment-specific config if provided, otherwise use global config
  const actualModuleSpacing = segmentConfig?.moduleSpacing ?? moduleSpacing;
  const actualRowSpacing = segmentConfig?.rowSpacing ?? rowSpacingMultiplier;
  const actualTargetCount = segmentConfig?.targetCount ?? targetModuleCount;

  const newModules: SolarModule[] = [];

  // Row spacing uses the multiplier for all modes
  // This respects the Row Spacing control in the UI
  const rowSpacing = effectiveHeight * actualRowSpacing;

  // Use segment rotation if provided, otherwise default to 0
  const moduleRotation = segmentRotation ?? 0;

  // Work in rotated coordinate space (using METERS for proper rotation)
  // This fixes skewing caused by mixing lat/lng degrees which have different scales
  const radians = (moduleRotation * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  // Helper: Transform point from rotated meter-space to lat/lng
  const transformToLatLng = (rotXMeters: number, rotYMeters: number): [number, number] => {
    // Rotate from meter-space back to north/east meter-space
    const northMeters = rotXMeters * cos - rotYMeters * sin;
    const eastMeters = rotXMeters * sin + rotYMeters * cos;
    // Convert meters to degrees
    const lat = centerLat + northMeters * latDegPerMeter;
    const lng = centerLng + eastMeters * lngDegPerMeter;
    return [lat, lng];
  };

  // Helper: Transform point from lat/lng to rotated meter-space
  const transformToRotated = (lat: number, lng: number): [number, number] => {
    // Convert degrees to meters
    const northMeters = (lat - centerLat) / latDegPerMeter;
    const eastMeters = (lng - centerLng) / lngDegPerMeter;
    // Rotate to aligned meter-space
    const rotXMeters = northMeters * cos + eastMeters * sin;
    const rotYMeters = -northMeters * sin + eastMeters * cos;
    return [rotXMeters, rotYMeters];
  };

  // Transform box to rotated meter-space to find bounds
  const rotatedBox = box.map(([lat, lng]) => transformToRotated(lat, lng));
  const minRotX = Math.min(...rotatedBox.map(([x]) => x));
  const maxRotX = Math.max(...rotatedBox.map(([x]) => x));
  const minRotY = Math.min(...rotatedBox.map(([, y]) => y));
  const maxRotY = Math.max(...rotatedBox.map(([, y]) => y));

  // Apply segment edge setback (in meters; rotated space is in meters).
  // Free-field uses half of the configured edgeSetback (no real "edge" of a roof,
  // so we keep less margin to allow edge-to-edge fitting).
  const segmentEdgeSetback = freeFieldMode ? edgeSetback / 2 : edgeSetback;

  const workingMinRotX = minRotX + segmentEdgeSetback;
  const workingMaxRotX = maxRotX - segmentEdgeSetback;
  const workingMinRotY = minRotY + segmentEdgeSetback;
  const workingMaxRotY = maxRotY - segmentEdgeSetback;

  // Module dimensions are already in meters
  const halfHeight = effectiveHeight / 2;
  const halfWidth = effectiveWidth / 2;

  // Start placing modules from top-left corner (after setback)
  const startRotX = workingMinRotX + halfHeight;
  const startRotY = workingMinRotY + halfWidth;

  // Place modules in top-left aligned grid (all in meters)
  let currentRotX = startRotX;

  while (currentRotX + halfHeight <= workingMaxRotX) {
    let currentRotY = startRotY;

    while (currentRotY + halfWidth <= workingMaxRotY) {
      // Transform back to lat/lng
      const [lat, lng] = transformToLatLng(currentRotX, currentRotY);
      const modulePosition: [number, number] = [lat, lng];
      const moduleRect = createModuleRectangle(modulePosition, moduleWidth, moduleHeight, moduleOrientation, moduleRotation);

      // Check if module is inside the bounds
      // Free-field mode: only check box containment (no polygon check)
      // Roof mode: check both box AND polygon containment
      const moduleCenterInBox = isPointInPolygon(modulePosition, box);
      const allCornersInBox = moduleRect.slice(0, -1).every(corner =>
        isPointInPolygon(corner, box)
      );

      let isValidPlacement = moduleCenterInBox && allCornersInBox;

      // In roof-attached mode, also check polygon containment
      if (!freeFieldMode && polygonCoords.length > 0) {
        const moduleCenterInPolygon = isPointInPolygon(modulePosition, polygonCoords);
        const allCornersInPolygon = moduleRect.slice(0, -1).every(corner =>
          isPointInPolygon(corner, polygonCoords)
        );
        isValidPlacement = isValidPlacement && moduleCenterInPolygon && allCornersInPolygon;
      }

      if (isValidPlacement) {
        // Check for collision with obstructions
        let overlapsObstruction = false;

        for (const obstruction of obstructions) {
          // Check if module center is inside obstruction
          if (isPointInPolygon(modulePosition, obstruction)) {
            overlapsObstruction = true;
            break;
          }

          // Check if any module corner is inside obstruction
          const moduleCornersInside = moduleRect.slice(0, -1).some(corner =>
            isPointInPolygon(corner, obstruction)
          );
          if (moduleCornersInside) {
            overlapsObstruction = true;
            break;
          }

          // Check if any obstruction corner is inside module
          const obstructionCornersInside = obstruction.slice(0, -1).some(corner =>
            isPointInPolygon(corner, moduleRect.slice(0, -1))
          );
          if (obstructionCornersInside) {
            overlapsObstruction = true;
            break;
          }

          // Also check clearance distance for proximity
          for (let i = 0; i < moduleRect.length - 1; i++) {
            const [modLat, modLng] = moduleRect[i];

            for (let j = 0; j < obstruction.length - 1; j++) {
              const [obsLat, obsLng] = obstruction[j];
              const distMeters = Math.sqrt(
                Math.pow((modLat - obsLat) / latDegPerMeter, 2) +
                Math.pow((modLng - obsLng) / lngDegPerMeter, 2)
              );

              if (distMeters < obstructionSetback) {
                overlapsObstruction = true;
                break;
              }
            }

            if (overlapsObstruction) break;
          }

          if (overlapsObstruction) break;
        }

        if (!overlapsObstruction) {
          newModules.push({
            position: modulePosition,
            width: moduleWidth,
            height: moduleHeight,
            orientation: moduleOrientation,
            rotation: moduleRotation,
            azimuth: moduleRotation,
            moduleBrand: panelInfo?.brand,
            moduleModel: panelInfo?.model,
            modulePowerWatts: panelInfo?.power_watts || modulePowerWatts,
          });
        }
      }

      // Move to next column (in meters)
      currentRotY += effectiveWidth + actualModuleSpacing;
    }

    // Move to next row (in meters)
    currentRotX += rowSpacing;
  }

  // Apply target module count constraint only if requested
  if (applyFillPercentage && actualTargetCount !== null && actualTargetCount !== undefined) {
    return newModules.slice(0, actualTargetCount);
  }

  return newModules;
}
